import type { CustomEventName } from '../utils/tracking'
import type { ProductLinkKey } from './productLinks'
import type { FlowKind, SelectionSource } from '../utils/funnelState'

/*
 * Checkout negotiation. Cart links live in data/productLinks.ts.
 *
 * Only one flow runs per checkout, each flow at most once per session, and an
 * accepted upgrade never automatically opens another ladder.
 *
 *   1 X1, no lifetime ($49.95 + shipping)     → $59.90 → $57.90 → $49.95 · stop
 *   1 X1 + lifetime ($69.90 + shipping)       → direct to cart, unless the customer taps
 *                                               "Want free shipping?" → $99.90 → $69.90 + free shipping · stop
 *   2 X1 ($99.90)                             → free third X1 → optional crew pricing link
 *   Direct 3 X1 ($99.90)                      → 6 for $179.90 → 5 for $149.90 → 4 for $119.90 · stop
 *   Direct 6 X1 ($199.80)                     → 12 for $359.90 → 10 for $299.90 → 8 for $239.90 · stop
 *   Direct 30 X1 ($999)                       → 60 for $1,799 → 50 for $1,499 → 40 for $1,199 · stop
 *
 * Standard shipping is only an ESTIMATE (typically around $6.95). Every
 * difference that depends on it is labeled "about" / "roughly".
 */

export const SINGLE_PRICE = 49.95
export const LIFETIME_UPGRADE_PRICE = 19.95
export const SINGLE_WITH_LIFETIME_PRICE = 69.9
export const TRIO_PRICE = 99.9
export const CREW_PRICE = 199.8
export const BUSINESS_PRICE = 999
/** Two public 30-packs recognized as one 60-system deployment. */
export const SIXTY_PRICE = 1799

/** Typical standard shipping on orders that don't ship free. A sales estimate, never a promise. */
export const ESTIMATED_SHIPPING = 6.95
export const SHIPPING_ESTIMATE_NOTE =
  '*Estimate based on typical standard shipping of around $6.95. Final shipping is shown at checkout. Taxes, if applicable, calculated at checkout.'

const fmt = (value: number) =>
  `$${value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`

/** Where a button finally sends the customer. Always one cart line: accepting replaces, never stacks. */
export interface CartTarget {
  link: ProductLinkKey
  quantity: number
  name: string
  /** Product total for this cart line, before shipping and tax. */
  value: number
}

export interface OrderSummary {
  label: string
  detail: string
  total: number
  coverage: string
  /** true = free standard shipping; false = calculated at checkout (typically around $6.95) */
  freeShipping: boolean
}

/** Shown after accepting a step instead of routing straight to the cart. */
export interface AcceptConfirmation {
  headline: string
  body: string
  cta: string
  /** Non-blocking, customer-initiated next opportunity. */
  followUp?: { label: string; flow: FlowKind; source: SelectionSource }
}

export interface UpsellStep {
  id: string
  plate: string
  headline: string
  highlight: string
  intro: string
  current: OrderSummary
  upgrade: OrderSummary
  /** Headline difference: "Same price" / "About $23 more before tax*" / "$80 more". */
  extra: string
  /** Exact product-price change, the estimated shipping saved, and the approximate real difference. */
  economics?: { productDiff: string; shippingSaved: string; result: string }
  includes: string[]
  /** Warranty price ladder on the lifetime-only offers. */
  valueLine?: { normal: string; today: string; todayLabel: string; shipping: string }
  /** Spells out that the number shown is the new product total, not the extra charge. */
  clarification: string
  lifetimeMedal?: boolean
  accept: string
  decline: string
  target: CartTarget
  /** Legacy per-step event names. Generic upsell_offer_* events always fire too. */
  events?: { viewed: CustomEventName; accepted: CustomEventName; declined: CustomEventName }
  confirmOnAccept?: AcceptConfirmation
}

export interface UpsellFlow {
  kind: FlowKind
  source: SelectionSource
  steps: UpsellStep[]
  /** After the last "no thanks": exactly what the customer originally picked. */
  fallback: CartTarget
}

const SHIPPING_SAVED = 'About $6.95*'

const packSummary = (units: number, total: number): OrderSummary => ({
  label: `${units} X1 complete systems`,
  detail: `${units * 3} lenses · ${fmt(total / units)} per X1`,
  total,
  coverage: `Lifetime on all ${units}`,
  freeShipping: true,
})

const trioIncludes = [
  '3 complete X1 systems',
  '9 interchangeable lenses: 3 Clear, 3 Amber, 3 Smoke',
  'Lifetime Replacement Warranty on all 3',
  'Free standard shipping',
  'Free mystery gift',
]

// ---------------------------------------------------------------------------
// 1 X1 without lifetime
// ---------------------------------------------------------------------------

function singleNoLifetimeFlow(source: SelectionSource): UpsellFlow {
  const current: OrderSummary = {
    label: '1 X1 complete system',
    detail: '$49.95 + shipping · roughly $56.90 before tax*',
    total: SINGLE_PRICE,
    coverage: 'One-year coverage',
    freeShipping: false,
  }
  const protectedSummary = (total: number, detail: string): OrderSummary => ({
    label: '1 X1 + Lifetime',
    detail,
    total,
    coverage: 'Lifetime coverage',
    freeShipping: true,
  })
  const lifetimeIncludes = [
    'Everything in your current order',
    'Lifetime Replacement Warranty. Break it. We replace it. Terms apply.',
    'Free standard shipping',
  ]
  return {
    kind: 'single-no-lifetime',
    source,
    steps: [
      {
        id: 'single-lifetime-5990',
        plate: 'Before you go',
        headline: 'Before you go.',
        highlight: 'Lifetime coverage + free shipping.',
        intro:
          "Your X1 is $49.95 and standard shipping is typically around $6.95, so you're already expecting to spend roughly $56.90 before tax. For only about $3 more, get the Lifetime Replacement Warranty and we'll cover standard shipping.",
        current,
        upgrade: protectedSummary(59.9, '$49.95 + $9.95 = $59.90 total'),
        extra: 'About $3 more before tax*',
        economics: { productDiff: '+$9.95', shippingSaved: SHIPPING_SAVED, result: 'About $3 more' },
        includes: lifetimeIncludes,
        valueLine: { normal: '$19.95', today: '$9.95', todayLabel: 'Private upgrade', shipping: 'Included' },
        clarification: 'You are not paying $59.90 more. Your new product total is $59.90.',
        lifetimeMedal: true,
        accept: 'Yes, lifetime + free shipping',
        decline: 'No thanks, keep my standard X1',
        target: {
          link: 'singleX1LifetimePrivate5990',
          quantity: 1,
          name: 'GritVue X1 Complete System + Lifetime Replacement Warranty + Free Shipping',
          value: 59.9,
        },
        events: {
          viewed: 'single_lifetime_995_viewed',
          accepted: 'single_lifetime_995_accepted',
          declined: 'single_lifetime_995_declined',
        },
      },
      {
        id: 'single-lifetime-5790',
        plate: 'Final private offer',
        headline: 'One final deal.',
        highlight: 'Lifetime + free shipping for $57.90.',
        intro:
          "Your standard X1 is $49.95 and shipping is typically around $6.95, so you're already expecting to spend roughly $56.90 before tax. For only about $1 more, get the Lifetime Replacement Warranty and free standard shipping.",
        current,
        upgrade: protectedSummary(57.9, '$49.95 + $7.95 = $57.90 total'),
        extra: 'About $1 more before tax*',
        economics: { productDiff: '+$7.95', shippingSaved: SHIPPING_SAVED, result: 'About $1 more' },
        includes: lifetimeIncludes,
        valueLine: { normal: '$19.95', today: '$7.95', todayLabel: 'Final upgrade', shipping: 'Included' },
        clarification: 'You are not paying $57.90 more. Your new product total is $57.90.',
        lifetimeMedal: true,
        accept: 'Yes, protect my X1 for life',
        decline: 'No thanks, continue with $49.95',
        target: {
          link: 'singleX1LifetimeFinal5790',
          quantity: 1,
          name: 'GritVue X1 Complete System + Lifetime Replacement Warranty + Free Shipping · Final private offer',
          value: 57.9,
        },
        events: {
          viewed: 'single_lifetime_795_viewed',
          accepted: 'single_lifetime_795_accepted',
          declined: 'single_lifetime_795_declined',
        },
      },
    ],
    fallback: { link: 'singleX1', quantity: 1, name: '1 GritVue X1 Complete System', value: SINGLE_PRICE },
  }
}

// ---------------------------------------------------------------------------
// 1 X1 + lifetime · only when the customer taps "Want free shipping?"
// ---------------------------------------------------------------------------

function singleLifetimeShippingFlow(source: SelectionSource): UpsellFlow {
  const current: OrderSummary = {
    label: '1 X1 + Lifetime',
    detail: '$69.90 + shipping · roughly $76 to $77 before tax*',
    total: SINGLE_WITH_LIFETIME_PRICE,
    coverage: 'Lifetime coverage',
    freeShipping: false,
  }
  return {
    kind: 'single-lifetime-shipping',
    source,
    steps: [
      {
        id: 'lifetime-trio-9990',
        plate: 'Want free shipping?',
        headline: "Here's how.",
        highlight: 'Get 3 for $99.90.',
        intro:
          "You're already expecting to spend around $76 to $77 before tax. For roughly $23 more, get 3 complete X1 systems instead of one, lifetime coverage on all 3, 9 interchangeable lenses and free standard shipping. Your 3rd X1 is free.",
        current,
        upgrade: { ...packSummary(3, TRIO_PRICE), label: '3 X1 + Lifetime on all 3' },
        extra: 'Roughly $23 more before tax*',
        economics: { productDiff: '+$30.00', shippingSaved: SHIPPING_SAVED, result: 'About $23 more' },
        includes: trioIncludes,
        clarification: 'You are not paying $99.90 more. Your new product total is $99.90.',
        lifetimeMedal: true,
        accept: 'Yes, get 3 for $99.90',
        decline: 'No thanks, keep my current order',
        target: { link: 'threeX1Public9990', quantity: 1, name: 'Buy 2, Get 1 Free · 3 complete systems', value: TRIO_PRICE },
        events: {
          viewed: 'three_x1_lifetime_9990_viewed',
          accepted: 'three_x1_lifetime_9990_accepted',
          declined: 'three_x1_lifetime_9990_declined',
        },
      },
      {
        id: 'lifetime-free-shipping-6990',
        plate: 'Shipping is on us',
        headline: 'You made it this far.',
        highlight: 'Shipping is on us.',
        intro:
          "Keep the X1 you already chose. Keep your Lifetime Replacement Warranty. We'll cover standard shipping. Your product total stays $69.90.",
        current,
        upgrade: {
          label: '1 X1 + Lifetime',
          detail: 'Same X1 · same coverage',
          total: SINGLE_WITH_LIFETIME_PRICE,
          coverage: 'Lifetime coverage',
          freeShipping: true,
        },
        extra: 'Same product total · about $6.95 less*',
        economics: { productDiff: '$0.00', shippingSaved: SHIPPING_SAVED, result: 'About $6.95 less' },
        includes: [
          'Your 1 X1 complete system',
          'Lifetime Replacement Warranty. Break it. We replace it. Terms apply.',
          'Free standard shipping',
        ],
        clarification: 'Your product total stays $69.90. Standard shipping is on us.',
        lifetimeMedal: true,
        accept: 'Claim my free shipping',
        decline: 'Continue with my original order',
        target: {
          link: 'singleX1LifetimeFreeShipping6990',
          quantity: 1,
          name: 'GritVue X1 Complete System + Lifetime Replacement Warranty + Free Shipping',
          value: SINGLE_WITH_LIFETIME_PRICE,
        },
        events: {
          viewed: 'single_lifetime_shipping_rescue_viewed',
          accepted: 'single_lifetime_shipping_rescue_accepted',
          declined: 'single_lifetime_shipping_rescue_declined',
        },
      },
    ],
    fallback: {
      link: 'singleX1LifetimeNormal6990',
      quantity: 1,
      name: 'GritVue X1 Complete System + Lifetime Replacement Warranty',
      value: SINGLE_WITH_LIFETIME_PRICE,
    },
  }
}

// ---------------------------------------------------------------------------
// 2 X1 → free third
// ---------------------------------------------------------------------------

function twoSystemsFlow(source: SelectionSource): UpsellFlow {
  return {
    kind: 'two-systems',
    source,
    steps: [
      {
        id: 'free-third',
        plate: 'You just unlocked a free X1',
        headline: 'Congratulations.',
        highlight: 'Your 3rd X1 is free.',
        intro: 'Why take 2 when you can get 3 for the same $99.90?',
        current: {
          label: '2 complete X1 systems',
          detail: '6 lenses',
          total: TRIO_PRICE,
          coverage: 'One-year coverage',
          freeShipping: true,
        },
        upgrade: {
          label: '3 complete X1 systems',
          detail: '9 lenses · mystery gift',
          total: TRIO_PRICE,
          coverage: 'Lifetime on all 3',
          freeShipping: true,
        },
        extra: 'Same product price',
        includes: [
          '3 complete X1 systems',
          '9 interchangeable lenses: 3 Clear, 3 Amber, 3 Smoke',
          'Lifetime Replacement Warranty on all 3 systems',
          'Free standard shipping',
          'Free mystery gift',
        ],
        clarification: 'Your 3rd X1 is completely free. You pay the same $99.90.',
        lifetimeMedal: true,
        accept: 'Claim my free X1',
        decline: 'No thanks, I only want 2 for $99.90',
        target: { link: 'threeX1Public9990', quantity: 1, name: 'Buy 2, Get 1 Free · 3 complete systems', value: TRIO_PRICE },
        events: {
          viewed: 'two_x1_free_third_viewed',
          accepted: 'two_x1_free_third_accepted',
          declined: 'two_x1_free_third_declined',
        },
        confirmOnAccept: {
          headline: 'Your 3rd X1 is free.',
          body: '3 complete X1 systems · 9 lenses · lifetime coverage on all 3 · free standard shipping · $99.90',
          cta: 'Continue to checkout',
          followUp: {
            label: 'Need more for your crew? See private crew pricing',
            flow: 'trio-expansion',
            source: 'free_third_claimed',
          },
        },
      },
    ],
    fallback: { link: 'singleX1', quantity: 2, name: '2 GritVue X1 complete systems', value: TRIO_PRICE },
  }
}

// ---------------------------------------------------------------------------
// Quantity expansion ladders (all free shipping, so every difference is exact)
// ---------------------------------------------------------------------------

interface Rung {
  units: number
  total: number
  link: ProductLinkKey
  plate: string
  headline: string
  highlight: string
  intro: string
  accept: string
}

interface LadderBase {
  units: number
  total: number
  link: ProductLinkKey
  name: string
  decline: string
}

function expansionFlow(kind: FlowKind, source: SelectionSource, base: LadderBase, rungs: Rung[]): UpsellFlow {
  const current = packSummary(base.units, base.total)
  return {
    kind,
    source,
    steps: rungs.map((rung) => ({
      id: `${kind}-${rung.units}`,
      plate: rung.plate,
      headline: rung.headline,
      highlight: rung.highlight,
      intro: rung.intro,
      current,
      upgrade: packSummary(rung.units, rung.total),
      extra: `${fmt(rung.total - base.total)} more`,
      includes: [
        `${rung.units} complete X1 systems`,
        `${rung.units * 3} interchangeable lenses`,
        `Lifetime Replacement Warranty on all ${rung.units}`,
        'Free standard shipping',
      ],
      clarification: `You are not paying ${fmt(rung.total)} more. Your new product total is ${fmt(rung.total)}.`,
      lifetimeMedal: true,
      accept: rung.accept,
      decline: base.decline,
      target: { link: rung.link, quantity: 1, name: `GritVue X1 · ${rung.units} complete systems · private offer`, value: rung.total },
    })),
    fallback: { link: base.link, quantity: 1, name: base.name, value: base.total },
  }
}

function trioExpansionFlow(source: SelectionSource) {
  return expansionFlow(
    'trio-expansion',
    source,
    { units: 3, total: TRIO_PRICE, link: 'threeX1Public9990', name: 'Buy 2, Get 1 Free · 3 complete systems', decline: 'No thanks, keep my 3' },
    [
      {
        units: 6,
        total: 179.9,
        link: 'sixX1Private17990',
        plate: 'Private crew pricing',
        headline: 'Double your setup.',
        highlight: '6 for $179.90.',
        intro:
          "You're already taking 3. For $80 more, take 6 complete X1 systems, 18 interchangeable lenses, lifetime coverage on all 6 and free standard shipping.",
        accept: 'Yes, get 6 for $179.90',
      },
      {
        units: 5,
        total: 149.9,
        link: 'fiveX1Private14990',
        plate: 'Private crew pricing',
        headline: 'Not ready for 6?',
        highlight: 'Take 5.',
        intro: 'Two extra complete X1 systems, lifetime coverage on all 5 and free standard shipping. Only $50 more than your current 3-pack.',
        accept: 'Yes, get 5 for $149.90',
      },
      {
        units: 4,
        total: 119.9,
        link: 'fourX1Private11990',
        plate: 'Final crew offer',
        headline: 'One more X1.',
        highlight: 'Only $20 more.',
        intro: 'Take 4 complete systems instead of 3, with lifetime coverage on all 4 and free standard shipping.',
        accept: 'Yes, get 4 for $119.90',
      },
    ],
  )
}

function sixExpansionFlow(source: SelectionSource) {
  return expansionFlow(
    'six-expansion',
    source,
    { units: 6, total: CREW_PRICE, link: 'sixX1Public19980', name: 'Buy 4, Get 2 Free · 6 complete systems', decline: 'No thanks, keep my 6' },
    [
      {
        units: 12,
        total: 359.9,
        link: 'twelveX1Private35990',
        plate: 'Private crew pricing',
        headline: 'Double the crew.',
        highlight: '12 for $359.90.',
        intro:
          "You're already equipping 6. Move to 12 complete X1 systems, 36 lenses, lifetime coverage on all 12 and free standard shipping.",
        accept: 'Yes, get 12 for $359.90',
      },
      {
        units: 10,
        total: 299.9,
        link: 'tenX1Private29990',
        plate: 'Private crew pricing',
        headline: 'Not ready for 12?',
        highlight: 'Take 10.',
        intro: 'Four more complete X1 systems, 30 lenses in total, lifetime coverage on all 10 and free standard shipping.',
        accept: 'Yes, get 10 for $299.90',
      },
      {
        units: 8,
        total: 239.9,
        link: 'eightX1Private23990',
        plate: 'Final crew offer',
        headline: 'Final crew upgrade.',
        highlight: 'Add 2 more.',
        intro: 'Add 2 more complete X1 systems for about $40 more, with lifetime coverage on all 8 and free standard shipping.',
        accept: 'Yes, get 8 for $239.90',
      },
    ],
  )
}

function businessExpansionFlow(source: SelectionSource) {
  return expansionFlow(
    'business-expansion',
    source,
    { units: 30, total: BUSINESS_PRICE, link: 'thirtyX1Public99900', name: 'Buy 20, Get 10 Free · 30 complete systems', decline: 'No thanks, keep my 30' },
    [
      {
        units: 60,
        total: SIXTY_PRICE,
        link: 'sixtyX1BusinessPrivate179900',
        plate: 'Private deployment pricing',
        headline: 'Double your deployment.',
        highlight: 'Equip 60 for $1,799.',
        intro:
          '30 more complete X1 systems for $800 more than your current order. Save $199 versus buying two standard 30-packs ($1,998). About $29.98 per X1.',
        accept: 'Yes, equip 60 for $1,799',
      },
      {
        units: 50,
        total: 1499,
        link: 'fiftyX1BusinessPrivate149900',
        plate: 'Private deployment pricing',
        headline: "Let's scale it back.",
        highlight: 'Take 50.',
        intro: '20 more complete X1 systems, lifetime coverage on all 50 and free standard shipping. Only $500 more than your current order.',
        accept: 'Yes, equip 50 for $1,499',
      },
      {
        units: 40,
        total: 1199,
        link: 'fortyX1BusinessPrivate119900',
        plate: 'Final deployment offer',
        headline: 'One final crew upgrade.',
        highlight: 'Add 10 more.',
        intro: 'Add 10 more complete X1 systems for only $200 more, with lifetime coverage on all 40 and free standard shipping.',
        accept: 'Yes, equip 40 for $1,199',
      },
    ],
  )
}

export function buildFlow(kind: FlowKind, source: SelectionSource): UpsellFlow {
  switch (kind) {
    case 'single-no-lifetime':
      return singleNoLifetimeFlow(source)
    case 'single-lifetime-shipping':
      return singleLifetimeShippingFlow(source)
    case 'two-systems':
      return twoSystemsFlow(source)
    case 'trio-expansion':
      return trioExpansionFlow(source)
    case 'six-expansion':
      return sixExpansionFlow(source)
    case 'business-expansion':
      return businessExpansionFlow(source)
  }
}

export type PackCard = 'single' | 'trio' | 'crew' | 'business'

/**
 * Which flow a checkout click opens. Ladders only run for a DIRECT choice of
 * that pack at quantity 1; a pack reached by accepting a suggestion or an
 * upgrade goes straight to the cart.
 */
export function checkoutFlow(
  card: PackCard,
  quantity: number,
  options: { lifetime?: boolean; reachedBySwitch?: boolean } = {},
): { kind: FlowKind; source: SelectionSource } | null {
  if (card === 'single') {
    if (quantity === 2) return { kind: 'two-systems', source: 'two_unit_selection' }
    if (quantity === 1 && !options.lifetime) return { kind: 'single-no-lifetime', source: 'single_standard' }
    return null
  }
  if (quantity !== 1 || options.reachedBySwitch) return null
  if (card === 'trio') return { kind: 'trio-expansion', source: 'direct_three_pack' }
  if (card === 'crew') return { kind: 'six-expansion', source: 'direct_six_pack' }
  return { kind: 'business-expansion', source: 'direct_thirty_pack' }
}

export interface PackSuggestion {
  message: string
  action: string
  switchTo: 'trio' | 'crew' | 'business'
  /** true: the card's own checkout would charge more for the same or fewer units, so the switch replaces it. */
  replacesCheckout: boolean
}

/**
 * Surfaces an existing public pack whenever a manual quantity maps onto one,
 * so nobody pays more for the same units. Never invents a new discount.
 */
export function suggestionFor(card: 'single' | 'trio' | 'crew', quantity: number): PackSuggestion | null {
  if (card === 'single') {
    const total = fmt(SINGLE_PRICE * quantity)
    if (quantity === 3) {
      return {
        message: `3 singles would be ${total}. Buy 2, Get 1 Free gives you the same 3 X1 for $99.90, with lifetime coverage on all 3 and free shipping.`,
        action: 'Switch to 3 for $99.90',
        switchTo: 'trio',
        replacesCheckout: true,
      }
    }
    if (quantity >= 4 && quantity <= 6) {
      return {
        message: `${quantity} singles would be ${total}. Buy 4, Get 2 Free gives you 6 X1 for $199.80, with lifetime coverage on all 6 and free shipping.`,
        action: 'Switch to 6 for $199.80',
        switchTo: 'crew',
        replacesCheckout: true,
      }
    }
    if (quantity >= 7) {
      return {
        message: `${quantity} singles would be ${total}. Our crew packs cost less per X1.`,
        action: 'See available crew packs',
        switchTo: 'crew',
        replacesCheckout: true,
      }
    }
  }
  if (card === 'trio' && quantity === 2) {
    return {
      message: 'Two 3-packs are 6 X1 for $199.80, the same as one Buy 4, Get 2 Free pack in a single order line.',
      action: 'Switch to the 6-pack',
      switchTo: 'crew',
      replacesCheckout: false,
    }
  }
  if ((card === 'trio' && quantity === 10) || (card === 'crew' && quantity === 5)) {
    return {
      message: 'That is 30 X1 for $999.00, the same as one Business / Crew Pack in a single order line.',
      action: 'Switch to the Business / Crew Pack',
      switchTo: 'business',
      replacesCheckout: false,
    }
  }
  return null
}
